import fetchAPI from '../fetchAPI';
import { useQuery, keepPreviousData } from '@tanstack/react-query';

type useZazuConversationProps = {
  locationId?: string;
  contactId?: string;
  limit?: number;
};

/**
 * The stored conversation for one contact — zazu's provider-neutral record of
 * what was actually said. Unlike the audit trail it includes messages a human
 * negotiator typed, which zazu never handled and so never wrote an audit row
 * for.
 *
 * Keyed by location + contact, because that pair identifies a conversation. The
 * query stays disabled until both are set: without them the endpoint 400s, and
 * a half-filled filter should show nothing rather than an error.
 */
export const useZazuConversation = ({ locationId, contactId, limit = 200 }: useZazuConversationProps) => {
  const query = new URLSearchParams();

  if (locationId) query.append('locationId', locationId);
  if (contactId) query.append('contactId', contactId);
  query.append('limit', limit.toString());

  return useQuery({
    queryKey: ['ai/conversation', locationId, contactId, limit],
    queryFn: async () => {
      const response: iResponse<ZazuConversationDetail> = await fetchAPI({
        method: 'GET',
        route: `ai/conversation`,
        queryString: query.toString(),
        isZazu: true
      });

      return response;
    },
    enabled: Boolean(locationId && contactId),
    placeholderData: keepPreviousData
  });
};

export default useZazuConversation;
